/** @jsx jsx */
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Box, Button, Flex, jsx, Spinner, Text } from 'theme-ui'
import { useCart, useTranslate } from '@chordcommerce/gatsby-theme-autonomy'
import FormInputWithLabel from '~/components/Generic/Form/InputWithLabel'
import { useAria } from '~/hooks/components/use-aria'

const PromoCode = ({ allPromotions, promotion }) => {
  const { updateAriaLive } = useAria()
  const translate = useTranslate()
  const { addPromoCode, promoCode, removePromoCode } = useCart()
  const [isExpanded, setIsExpanded] = useState(!!promotion)

  const { register, handleSubmit, errors } = useForm({
    defaultValues: {
      promoCode: promotion ? promotion.code : '',
    },
  })

  const { isFetching } = promoCode
  const [apiError, setApiError] = useState(null)

  const discountPromotions = allPromotions.filter(({ amount }) => !!amount)

  const onSubmit = async (data, e) => {
    if (promotion) {
      try {
        setApiError(null)
        await removePromoCode({ promoCode: promotion.code })
        e.target.reset()
      } catch (error) {
        setApiError(error)
        updateAriaLive(error)
      }
    } else {
      try {
        setApiError(null)
        await addPromoCode({ promoCode: data.promoCode })
      } catch (error) {
        setApiError(error)
      }
    }
  }

  return (
    <Box>
      <Button
        variant="reset"
        onClick={() => setIsExpanded(true)}
        sx={{ display: isExpanded ? 'none' : 'block' }}
      >
        <Text
          as="strong"
          variant="body2"
          sx={{
            color: 'brandPrimary',
            fontWeight: 'bold',
            textDecoration: 'underline',
          }}
        >
          {translate('cart.promo.header')}
        </Text>
      </Button>
      <Box
        as="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ display: isExpanded ? 'block' : 'none' }}
      >
        <Flex sx={{ justifyContent: 'space-between' }}>
          <Box mr={2} sx={{ flex: '1' }}>
            <FormInputWithLabel
              label={translate('cart.promo_code')}
              title={translate('cart.promo_code')}
              aria-label={translate('cart.promo_code')}
              placeholder={translate('cart.promo_code')}
              name="promoCode"
              value={promotion?.code}
              disabled={promotion}
              ref={register({ required: true })}
            />
          </Box>
          <Button variant="secondary" px={3}>
            {isFetching && (
              <Spinner
                size="15"
                color="inherit"
                sx={{
                  '@media (prefers-reduced-motion)': {
                    circle: {
                      animationIterationCount: 8,
                    },
                  },
                }}
              />
            )}
            {!isFetching && (
              <Text>
                {promotion
                  ? translate('cart.remove_promo_code_button')
                  : translate('cart.apply_promo_code_button')}
              </Text>
            )}
          </Button>
        </Flex>
        {errors.promoCode && !apiError && (
          <Text as="p" mt={1} variant="error">
            {translate('validation.required_field')}
          </Text>
        )}
        {promotion &&
          discountPromotions &&
          discountPromotions.length > 1 &&
          !errors.promoCode &&
          !apiError && (
            <Text as="p" mt={1} variant="error">
              {translate('cart.promo.multiple')}
            </Text>
          )}
      </Box>
      {apiError && (
        <Text as="p" mt={2} variant="error">
          {apiError.message}
        </Text>
      )}
    </Box>
  )
}

PromoCode.propTypes = {
  promotion: PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    amount: PropTypes.number,
    displayAmount: PropTypes.string,
  }),
}

export default PromoCode
